import { TableWithStoredSettingsAndLayoutSettings } from '@Components/Tables/TableWithStoredSettingsAndLayoutSettings';
import { Permission } from '@Constants/Permission.g';
import { initialOrderSorting, orderColumns } from '@Constants/TableColumns';
import { useHasPermission } from '@Contexts/appContext';
import { useFeatureFlags } from '@Contexts/featureFlags';
import { useGetOrders } from '@Hooks/Orders';
import { type IHasTableModes } from '@Hooks/useTableModes';
import { EnumFrontendLayoutSetting, type ApiOrder } from '@Model';
import { Button, iconSizeSm, SingleSelect, type ToolbarOptions } from '@synqo/frontend-component-library';
import { IconPlus } from '@tabler/icons-react';
import { useCallback, useMemo, type FC } from 'react';
import { containerActionsColumn } from '../../../columns';
import { useClientNavigate } from '../../../../Routing/useClientNavigate';
import { type UseOrderTableModes } from './useOrderTableModes';

interface FrameListOrdersProps extends IHasTableModes<UseOrderTableModes> {}

export const FrameListOrders: FC<FrameListOrdersProps> = ({ tableModeValue, setTableMode, availableTableModes }) => {
    const hasPermission = useHasPermission();
    const navigate = useClientNavigate();

    const { isFeatureEnabled } = useFeatureFlags();
    const showsContainers =
        isFeatureEnabled('CONTAINER_SERVICE') && hasPermission(Permission.AuftragsbearbeitungContainerActionsRead);

    const loadOrders = useGetOrders(undefined, showsContainers);
    const columns = useMemo(
        () => (showsContainers ? [...orderColumns, containerActionsColumn] : orderColumns),
        [showsContainers],
    );

    const canPlaceOrders = hasPermission(Permission.AuftragsbearbeitungRequisitionsCreate);

    const toolbarElements = useCallback(
        (elements: ToolbarOptions[]) => [
            ...elements,
            canPlaceOrders && (
                <Button
                    key="placeOrder"
                    appearance="filled"
                    variant="primary"
                    onClick={() => navigate('/requisitions/new')}
                >
                    <IconPlus size={iconSizeSm} />
                    Neue Bestellung
                </Button>
            ),
            availableTableModes.length > 1 && (
                <SingleSelect<UseOrderTableModes>
                    key="tableMode"
                    name="tableMode"
                    items={availableTableModes}
                    value={tableModeValue}
                    onChange={setTableMode}
                />
            ),
        ],
        [canPlaceOrders, navigate, availableTableModes, tableModeValue, setTableMode],
    );

    return (
        <TableWithStoredSettingsAndLayoutSettings<ApiOrder>
            layoutSettingsContext={EnumFrontendLayoutSetting.OrdersTableLayout}
            name="OrderList"
            columns={columns}
            loader={loadOrders}
            canCreate={hasPermission(Permission.AuftragsbearbeitungOrdersCreate)}
            useOnClickNavigation
            initialSorting={initialOrderSorting}
            toolbarElements={toolbarElements}
            fullSize
        />
    );
};
